import { queryOptions, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import { queryKeys } from "#/hooks/query-keys";
import { fetchPosts, type Post } from "#/lib/api/posts";
import { SUPPORTED_LOCALES, type SupportedLocale } from "#/lib/locale";
import { localeAtom } from "#/stores/locale-store";

/** 훅과 라우트 로더(SSR prefetchQuery)가 공유하는 쿼리 정의 — 키/fetch 드리프트 방지. */
export function learnPostsQueryOptions(locale: SupportedLocale) {
	return queryOptions({
		queryKey: queryKeys.posts.list(locale),
		queryFn: () => fetchPosts(locale),
		staleTime: 60 * 60 * 1000,
		gcTime: 24 * 60 * 60 * 1000,
	});
}

/**
 * All published learn posts in the active locale. Cached aggressively (1h) —
 * content changes on CMS cadence, and a stale hub is harmless.
 *
 * Placeholder 체인 (언어 전환 로더 깜빡임 방지):
 * ① keepPreviousData — 같은 컴포넌트 인스턴스 안에서의 키 전환(zh 등)
 * ② 다른 locale의 목록 캐시 — en↔ko 전환은 라우트 트리(/learn ↔ /ko/learn)를
 *    갈아타며 리마운트되어 ①의 previous 체인이 끊기므로, 캐시에서 직접 폴백
 */
export function useLearnPosts() {
	const locale = useAtomValue(localeAtom);
	const queryClient = useQueryClient();
	return useQuery({
		...learnPostsQueryOptions(locale),
		placeholderData: (previousData) => {
			// 목록 키는 locale만 다르므로 previous는 항상 "같은 목록, 다른 언어" — 안전
			if (previousData) return previousData;
			for (const other of SUPPORTED_LOCALES) {
				if (other === locale) continue;
				const cached = queryClient.getQueryData<Post[]>(
					queryKeys.posts.list(other),
				);
				if (cached) return cached;
			}
			return undefined;
		},
	});
}
